import { useState, useEffect } from 'react';
import { m, AnimatePresence } from 'framer-motion';
import { ArrowUpRight, X } from 'lucide-react';

const cases = [
  {
    id: 1,
    title: "Интеграция CRM и телефонии",
    client: "Крупная сеть клиник в Красноярске",
    description: "Разработали middleware API для связи медицинской системы (МИС), IP-телефонии и Bitrix24. Сократили время обработки звонков на 40% и исключили потерю лидов.",
    tags: ["Bitrix24", "Python/FastAPI", "Asterisk"],
    image: "/images/case1.jpg"
  },
  {
    id: 2,
    title: "Защищенная корпоративная сеть",
    client: "Торговая компания (15 филиалов)",
    description: "Спроектировали и внедрили отказоустойчивую сеть на базе MikroTik. Настроили L2 VPN между филиалами, резервирование каналов и систему мониторинга Zabbix.",
    tags: ["MikroTik", "VPN", "Zabbix", "Инфраструктура"],
    image: "/images/case2.jpg"
  },
  {
    id: 3,
    title: "Переезд в облако (VPS/VDS)",
    client: "Федеральный интернет-магазин",
    description: "Мигрировали высоконагруженный проект с устаревшего физического сервера на наш кластер NVMe VPS. Настроили Docker Swarm, балансировку Nginx и защиту от DDoS.",
    tags: ["VPS/VDS", "Docker", "Highload", "DDoS Protection"],
    image: "/images/hero.jpg"
  }
];

export const Cases = () => {
  const [selectedCase, setSelectedCase] = useState<typeof cases[0] | null>(null);

  useEffect(() => {
    if (selectedCase) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [selectedCase]);

  return (
    <section id="cases" className="py-24 bg-[#020617] relative border-t border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
          <div>
            <m.h2 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-3xl md:text-4xl font-extrabold text-white mb-4"
            >
              Реализованные проекты
            </m.h2>
            <m.p 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-lg text-slate-400 max-w-2xl"
            >
              Примеры того, как мы решаем сложные инфраструктурные и интеграционные задачи.
            </m.p>
          </div>
          <m.a 
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            href="#contact"
            className="inline-flex items-center gap-2 text-accent hover:text-white font-bold transition-colors"
          >
            <span>Обсудить ваш проект</span>
            <ArrowUpRight className="w-5 h-5" />
          </m.a>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {cases.map((item, index) => (
            <m.div
              key={item.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.15 }}
              onClick={() => setSelectedCase(item)}
              className="group cursor-pointer rounded-2xl overflow-hidden glass-panel border border-white/5 hover:border-accent/50 transition-all duration-500"
            >
              <div className="relative h-48 sm:h-64 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-t from-[#020617] via-transparent to-transparent z-10"></div>
                <img 
                  src={item.image} 
                  alt={item.title} 
                  className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute top-4 left-4 z-20 flex flex-wrap gap-2">
                  {item.tags.slice(0, 2).map((tag, idx) => (
                    <span key={idx} className="bg-black/50 backdrop-blur-md text-white text-xs font-semibold px-3 py-1 rounded-full border border-white/10">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
              
              <div className="p-6 md:p-8 relative">
                <div className="text-accent font-semibold text-sm mb-2">{item.client}</div>
                <h3 className="text-xl font-bold text-white mb-4 group-hover:text-accent transition-colors">{item.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed mb-6">
                  {item.description}
                </p>
                
                <div className="flex items-center text-white text-sm font-bold opacity-0 -translate-y-4 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
                  <span className="mr-2">Подробнее</span>
                  <ArrowUpRight className="w-4 h-4" />
                </div>
              </div>
            </m.div>
          ))}
        </div>
      </div>

      {/* Модальное окно кейса */}
      <AnimatePresence>
        {selectedCase && (
          <m.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-[#020617]/80 backdrop-blur-sm overflow-y-auto"
            onClick={() => setSelectedCase(null)}
          >
            <m.div 
              initial={{ scale: 0.95, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-[#0f172a] border border-white/10 rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-2xl relative flex flex-col"
            >
              <button 
                onClick={() => setSelectedCase(null)}
                className="absolute top-4 right-4 z-10 w-8 h-8 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-colors"
              >
                <X className="w-5 h-5" />
              </button>

              {/* Картинка */}
              <div className="w-full aspect-video md:aspect-[21/9] relative">
                <img 
                  src={selectedCase.image} 
                  alt={selectedCase.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0f172a] to-transparent"></div>
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="text-accent font-bold text-sm tracking-wider uppercase mb-2">{selectedCase.client}</div>
                  <h3 className="text-2xl md:text-3xl font-extrabold text-white">{selectedCase.title}</h3>
                </div>
              </div>

              {/* Описание */}
              <div className="p-6 md:p-10">
                <div className="flex flex-wrap gap-2 mb-6">
                  {selectedCase.tags.map((tag, idx) => (
                    <span key={idx} className="bg-white/5 text-slate-300 text-xs font-semibold px-3 py-1 rounded-full border border-white/10">
                      {tag}
                    </span>
                  ))}
                </div>

                <h4 className="text-xl font-bold text-white mb-4">О проекте</h4>
                <p className="text-slate-400 leading-relaxed whitespace-pre-line text-lg">
                  {selectedCase.description}
                </p>
                
                <div className="mt-10 pt-8 border-t border-white/10 text-center">
                  <a 
                    href="#contact"
                    onClick={() => setSelectedCase(null)}
                    className="inline-flex justify-center items-center px-8 py-3 text-base font-bold rounded-xl text-white bg-accent hover:bg-accent-hover transition-colors glow-effect"
                  >
                    Хочу такой же результат
                  </a>
                </div>
              </div>
            </m.div>
          </m.div>
        )}
      </AnimatePresence>
    </section>
  );
};